import React from 'react';
import { Box, Icons, Pill, Stack, SxProps } from '@/theme/index';

interface PillListItem {
  label: string;
  [key: string]: unknown;
}

interface PillLabelProps {
  /** Label to be rendered */
  label: string | React.ReactNode;
  /** Icon to be rendered */
  icon?: JSX.Element;
}

export interface PillListProps {
  /** List of items to be rendered as pills */
  items: PillListItem[];
  /** Icon to be rendered in the pills */
  icon?: JSX.Element;
  /** Custom styles for the root component */
  sx?: SxProps;
  /** Callback fired when a pill is clicked */
  onItemClicked?: (item: PillListItem) => void;
  /** Callback fired when the Clear All pill is clicked */
  onRemoveAll?: () => void;
}

/** A component that renders a label and an optional close icon. */
const PillLabel = ({ label, icon }: PillLabelProps) => {
  return (
    <Box component="span" display="flex" alignItems="center">
      {label}
      {icon && (
        <>
          &nbsp;
          <Box component="span" display="flex" mr={-0.5}>
            {icon}
          </Box>
        </>
      )}
    </Box>
  );
};

/**
 * A component that renders a list of items as pills.
 * @param items List of items to be rendered as pills.
 * @param icon Icon to be rendered in the pills.
 * @param sx Custom styles for the root component.
 * @param onItemClicked Callback fired when a pill is clicked.
 * @param onRemoveAll Callback fired when the Clear All pill is clicked.
 */
export const PillList: React.FC<PillListProps> = ({
  items,
  icon,
  sx,
  onItemClicked,
  onRemoveAll,
}) => {
  const noItems = items.length === 0;

  if (noItems) return null;

  const clearAll = onRemoveAll !== undefined;
  const clickableItems = onItemClicked !== undefined;

  const handleItemClick = (item: PillListItem) => {
    if (onItemClicked) onItemClicked(item);
  };

  const handleRemoveAll = () => {
    if (onRemoveAll) onRemoveAll();
  };

  return (
    <Stack
      direction="row"
      spacing={2}
      flexWrap="wrap"
      useFlexGap
      sx={sx}
      data-testid="pill-list"
    >
      {clearAll && (
        <Pill
          label={
            <PillLabel
              label="Clear All"
              icon={<Icons.Cancel fontSize="small" />}
            />
          }
          color="primary"
          onClick={() => handleRemoveAll()}
          size="small"
          sx={(theme) => ({
            padding: theme.spacing(0.4),
          })}
        />
      )}

      {items.map((item, index) => (
        <Pill
          key={`pill-${index}`}
          label={<PillLabel label={item.label} icon={icon} />}
          variant="filled"
          size="small"
          onClick={clickableItems ? () => handleItemClick(item) : undefined}
          sx={(theme) => ({
            backgroundColor: theme.palette.secondary.light,
            padding: theme.spacing(0.4),
            textTransform: 'capitalize',
            color: theme.palette.primary.main,
          })}
        />
      ))}
    </Stack>
  );
};

export default PillList;
